import { MetricaScript } from "@kupikupi/analytics/react";
import { LOCALES, YM_COUNTER_ID } from "@kupikupi/brand";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthBootstrap } from "./components/AuthBootstrap";
import { BackendLangBridge } from "./components/BackendLangBridge";
import { MetricaUserId } from "./components/MetricaUserId";
import { initI18n } from "./i18n";
import { registerApiAuthBridge } from "./lib/auth";
import { initMockTelegram } from "./lib/mock-tg-env";
import { getTelegramLanguageCode } from "./lib/telegram-auth";
import { router } from "./router";
import { initTelegram } from "./telegram";
import "./main.css";

/**
 * Mini App boot locale = Telegram user's language: Chinese users (`zh*`) see
 * Chinese, everyone else sees Russian. (Product call 2026-06-03 — the team
 * can't read Russian, so zh-speakers get zh; all other langs → ru.)
 *
 * `zh` only resolves when it's in the build's locale whitelist (VITE_LOCALES);
 * if not, fall back to ru so a stray zh never crashes initI18n on an unsupported
 * locale. Outside Telegram (no language_code) → ru.
 */
function resolveBootLocale(): "ru" | "zh" {
  const code = getTelegramLanguageCode();
  if (code?.toLowerCase().startsWith("zh") && LOCALES.includes("zh")) {
    return "zh";
  }
  return "ru";
}

async function bootstrap() {
  // Teach @kupikupi/api to read the Mini-App's JWT (kk_auth_token) for GET
  // auto-attach. Must run before any query so the first detail fetch is
  // authenticated and the backend stamps the real per-user `favorite` (#955).
  registerApiAuthBridge();
  initMockTelegram();
  initTelegram();
  await initI18n(resolveBootLocale());

  const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
  });

  const root = document.getElementById("root");
  if (!root) throw new Error("#root not found");
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthBootstrap>
          <BackendLangBridge>
            <MetricaScript counterId={YM_COUNTER_ID} />
            <MetricaUserId />
            <RouterProvider router={router} />
          </BackendLangBridge>
        </AuthBootstrap>
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap();
